import React, { useState, useEffect, useCallback } from "react";
import { TextInput, TextInputProps } from "@mantine/core";
import { formatPhoneNumber, validatePhoneNumber } from "../../utils/phoneUtils";

export interface ParsedPhoneNumber {
  areaCode: string;
  phoneNumber: string;
}

export interface StandardizedPhoneInputProps extends Omit<
  TextInputProps,
  "onChange" | "value"
> {
  value: string;
  onChange: (value: string) => void;
  onParsedChange?: (parsed: ParsedPhoneNumber | null) => void;
  onRawChange?: (raw: string) => void;
  error?: string;
  required?: boolean;
  label?: string;
  placeholder?: string;
}

/**
 * Standardized phone input component that:
 * - Formats phone numbers as user types: (555) 555-5555
 * - Provides parsed format (area_code, phone_number) for salon/business use
 * - Provides raw format (E.164: +1XXXXXXXXXX) for employees/signup use
 *
 * Usage:
 * - For salon/business: Use onParsedChange to get { areaCode, phoneNumber }
 * - For employees/signup: Use onRawChange to get E.164 format (+1XXXXXXXXXX)
 */
export default function StandardizedPhoneInput({
  value,
  onChange,
  onParsedChange,
  onRawChange,
  error,
  required = false,
  label = "Phone Number",
  placeholder = "(555) 555-5555",
  ...props
}: StandardizedPhoneInputProps) {
  const [displayValue, setDisplayValue] = useState(value || "");

  // Update display value when external value changes
  useEffect(() => {
    setDisplayValue(value || "");
  }, [value]);

  // Parse phone number into area code and phone number (for salon/business)
  const parsePhoneNumber = useCallback(
    (phone: string): ParsedPhoneNumber | null => {
      const cleaned = phone.replace(/\D/g, "");
      if (cleaned.length === 10) {
        return {
          areaCode: cleaned.slice(0, 3),
          phoneNumber: cleaned.slice(3),
        };
      }
      return null;
    },
    [],
  );

  // Format phone number to E.164 format (+1XXXXXXXXXX) for employees/signup
  const formatToE164 = useCallback((phone: string): string => {
    const digitsOnly = phone.replace(/\D/g, "");

    // If it already starts with 1 and has 11 digits, use as-is
    if (digitsOnly.length === 11 && digitsOnly.startsWith("1")) {
      return `+${digitsOnly}`;
    }

    // If it has 10 digits, add +1 prefix
    if (digitsOnly.length === 10) {
      return `+1${digitsOnly}`;
    }

    // Return with +1 prefix for other cases (let Cognito/API validate)
    return `+1${digitsOnly}`;
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedValue = formatPhoneNumber(inputValue);

    setDisplayValue(formattedValue);
    onChange(formattedValue);

    // Provide parsed format for salon/business use
    if (onParsedChange) {
      const parsed = parsePhoneNumber(formattedValue);
      onParsedChange(parsed);
    }

    // Provide raw E.164 format for employees/signup use
    if (onRawChange) {
      const raw = formatToE164(formattedValue);
      onRawChange(raw);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (props.onBlur) {
      props.onBlur(e);
    }

    // Validate on blur
    if (displayValue && !validatePhoneNumber(displayValue)) {
      // Error will be handled by parent component via error prop
    }
  };

  return (
    <TextInput
      label={label}
      required={required}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      {...props}
    />
  );
}
